/*! DarePouch — display-only multi-currency (v2: GBP · USD · CAD · AUD · CHF)
 * Converts EUR price DISPLAY to the visitor's local currency. Checkout stays EUR.
 * Inert unless window.DP_CURRENCY_ENABLED === true (theme flag) or ?dp_fx=1 (test).
 * Safe by design: only rewrites text nodes that are ENTIRELY a price (never prose),
 * is idempotent, and falls back to EUR on any doubt (stale rates, unknown country).
 *
 * The shopper reads a local price and, right under it, the exact EUR amount their card
 * will be charged — because the checkout is EUR and their bank statement will say so.
 */
(function () {
  "use strict";
  var P = new URLSearchParams(location.search);
  var TEST = P.get("dp_fx") === "1";
  if (!window.DP_CURRENCY_ENABLED && !TEST) return;            // feature flag / test gate

  // Currencies whose DISPLAY is live. Adding one here means adding it to fx_daily.py's
  // REQUIRED, to fx_healthcheck.mjs's MARKETS and to currency_cvr_weekly.py's MARKETS —
  // otherwise it ships unmonitored.
  var ACTIVE = { GBP: 1, USD: 1, CAD: 1, AUD: 1, CHF: 1 };
  // BG is absent on purpose: Bulgaria is on the euro, there is no BGN rate to fetch.
  var COUNTRY_CCY = { GB: "GBP", US: "USD", CA: "CAD", AU: "AUD", SE: "SEK", NO: "NOK",
    DK: "DKK", CH: "CHF", PL: "PLN", CZ: "CZK", HU: "HUF", RO: "RON" };
  // Fallback only — Shopify.country is server-set per visitor and wins. Kept wide enough
  // that the live markets resolve even when it is missing.
  var TZ_COUNTRY = {
    "Europe/London": "GB",
    "America/New_York": "US", "America/Chicago": "US", "America/Denver": "US",
    "America/Los_Angeles": "US", "America/Phoenix": "US", "America/Detroit": "US",
    "America/Anchorage": "US", "Pacific/Honolulu": "US",
    "America/Toronto": "CA", "America/Vancouver": "CA", "America/Edmonton": "CA",
    "America/Winnipeg": "CA", "America/Halifax": "CA", "America/St_Johns": "CA",
    "Australia/Sydney": "AU", "Australia/Melbourne": "AU", "Australia/Brisbane": "AU",
    "Australia/Perth": "AU", "Australia/Adelaide": "AU", "Australia/Hobart": "AU",
    "Australia/Darwin": "AU",
    "Europe/Stockholm": "SE", "Europe/Oslo": "NO", "Europe/Copenhagen": "DK",
    "Europe/Zurich": "CH", "Europe/Warsaw": "PL", "Europe/Prague": "CZ"
  };
  // CHF is written "CHF 4.90", code first; a no-break space so a narrow card never wraps it
  var FMT = {
    GBP: { s: "£", pre: true, d: 2, dot: "." }, USD: { s: "$", pre: true, d: 2, dot: "." },
    CAD: { s: "C$", pre: true, d: 2, dot: "." }, AUD: { s: "A$", pre: true, d: 2, dot: "." },
    CHF: { s: "CHF\u00a0", pre: true, d: 2, dot: "." }, SEK: { s: " kr", pre: false, d: 0, dot: "," },
    NOK: { s: " kr", pre: false, d: 0, dot: "," }, DKK: { s: " kr", pre: false, d: 0, dot: "," },
    PLN: { s: " zł", pre: false, d: 2, dot: "," }, CZK: { s: " Kč", pre: false, d: 0, dot: "," },
    HUF: { s: " Ft", pre: false, d: 0, dot: "," }, RON: { s: " lei", pre: false, d: 2, dot: "," }
  };

  // ── country → target currency ─────────────────────────────
  function region(s) { var m = /[-_]([A-Z]{2})/.exec(s || ""); return m ? m[1] : ""; }
  function detectCountry() {
    var forced = (P.get("dp_country") || "").toUpperCase();
    if (forced) return forced;
    try { if (window.Shopify && Shopify.country) return String(Shopify.country).toUpperCase(); } catch (e) {}
    try {
      var tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (TZ_COUNTRY[tz]) return TZ_COUNTRY[tz];
    } catch (e) {}
    var langs = (navigator.languages || [navigator.language || ""]);
    for (var i = 0; i < langs.length; i++) { var r = region(langs[i]); if (r) return r; }
    return "";
  }
  var override = null;
  try { override = localStorage.getItem("dp_ccy_override"); } catch (e) {}
  var country = detectCountry();
  var target = override || COUNTRY_CCY[country];
  if (!target || target === "EUR" || !ACTIVE[target]) return;   // EUR / unsupported → no-op

  // ── rates (localStorage cache, revalidate by ttl) ─────────
  function ratesUrl() {
    if (window.DP_FX_URL) return window.DP_FX_URL;              // Liquid-generated, always right
    // follow our own asset path so the fallback survives a theme renumbering
    var self = document.currentScript || document.querySelector('script[src*="darepouch-currency-display"]');
    if (self && self.src) return self.src.replace(/darepouch-currency-display\.js.*$/, "fx-rates.json");
    return "/cdn/shop/t/2/assets/fx-rates.json";
  }
  function withRates(cb) {
    var cached = null;
    try { cached = JSON.parse(localStorage.getItem("dp_fx_cache") || "null"); } catch (e) {}
    var fresh = cached && (Date.now() - cached._t) < (cached.ttl_hours || 24) * 3.6e6;
    if (fresh) return cb(cached);
    fetch(ratesUrl(), { cache: "no-cache" }).then(function (r) { return r.json(); }).then(function (fx) {
      fx._t = Date.now();
      try { localStorage.setItem("dp_fx_cache", JSON.stringify(fx)); } catch (e) {}
      cb(fx);
    }).catch(function () { if (cached) cb(cached); });          // network fail → last cache
  }

  // ── number parsing / formatting ───────────────────────────
  var PRICE_RE = /^\s*€?\s*(\d{1,3}(?:[.\s]\d{3})*|\d+)(?:,(\d{1,2}))?\s*€\s*$/;  // "24,99€", "1.234,56€"
  function eurToNumber(txt) {
    var m = PRICE_RE.exec(txt);
    if (!m) return null;
    var whole = m[1].replace(/[.\s]/g, "");
    return parseFloat(whole + "." + (m[2] || "0"));
  }
  function fmt(n, ccy) {
    var f = FMT[ccy]; var v = n.toFixed(f.d);
    if (f.dot !== ".") v = v.replace(".", f.dot);
    return f.pre ? f.s + v : v + f.s;
  }

  // ── Moon Bundle labels (Canada, 30/09/2026) ───────────────
  // The Moon Bundle cart drawer and tier cards write their amounts INSIDE short labels:
  // "4,56€ / Can", "1,70€ OFF", "-1,70€", "Checkout 22,80€". For the currencies listed here, and
  // only inside Moon Bundle's own elements, a label holding exactly ONE amount gets that amount
  // converted and keeps the rest of its words. Site prose never sits in a Moon Bundle element,
  // so "Save €5 on orders over €50" keeps its euros. Adding a currency here extends it to that
  // market — add it to test_dom.mjs's LABELLED block too.
  var LABELLED = { CAD: 1 };
  var TOKEN_RE = /([-\u2212]\s*)?(\d{1,3}(?:[.\s]\d{3})*|\d+)(?:,(\d{1,2}))?\s*€/;
  function inLabel(el) {
    return !!(LABELLED[target] && el && el.closest && el.closest('[class*="moonbundle"]'));
  }
  function oneAmount(txt) {
    var all = txt.match(new RegExp(TOKEN_RE.source, "g"));
    return !!all && all.length === 1;
  }

  // ── conversion ────────────────────────────────────────────
  var rate = 0, fmtEUR = function (n) { return n.toFixed(2).replace(".", ",") + "€"; };
  function convertLabel(node) {
    var raw = node.nodeValue, el = node.parentElement;
    if (!inLabel(el) || !oneAmount(raw)) return;
    if (el.closest('[aria-hidden="true"]') || el.closest(".dp-fx-note")) return;
    var m = TOKEN_RE.exec(raw);
    var eur = parseFloat(m[2].replace(/[.\s]/g, "") + "." + (m[3] || "0"));
    var sign = m[1] ? "-" : "";
    var out = sign + fmt(eur * rate, target);
    node.nodeValue = raw.replace(TOKEN_RE, function () { return out; });   // function: "$2" stays literal
    el.setAttribute("title", "≈ " + sign + fmtEUR(eur) + " · charged in EUR at checkout");
    el.setAttribute("data-dp-label-eur", String(m[1] ? -eur : eur));        // read by handoff()
  }
  function convertNode(node) {
    var raw = node.nodeValue;
    if (raw.indexOf("€") === -1) return;                       // our own output has no € → idempotent
    var eur = eurToNumber(raw);
    if (eur === null) { convertLabel(node); return; }          // not a bare price: a Moon Bundle label?
    if (eur < 0) return;                                       // 0,00€ included: an empty cart badge
    var el = node.parentElement;                               // still showing € among dollars looks broken
    if (!el) return;
    // aria-hidden marks machine-readable mirrors, not text for humans: the theme keeps a hidden
    // `.cart__total[data-cart-total]` that its own JS parses. Rewriting it would break the theme.
    if (el.closest && el.closest('[aria-hidden="true"]')) return;
    // Never re-scan our own output. The EUR line puts the amount in its own <strong>, so that
    // node IS entirely a price and the scanner happily turned "Charged in EUR: 4,80€" into
    // "Charged in EUR: $5.47" — the one number that must stay in euros.
    if (el.closest && el.closest(".dp-fx-note")) return;
    // No "already done" flag: apps re-render prices in place (Moon Bundle rewrites its tiers on
    // every selection, the theme rewrites the cart badge). A one-shot flag left those back in EUR.
    el.setAttribute("data-dp-fx", "1");
    el.setAttribute("data-dp-eur", String(eur));
    // replacement FUNCTION, never a string: "$28.46" passed as a string would have its "$2"
    // read as a capture-group reference and come out as "998.46". £ never hit this; $ does.
    var out = fmt(eur * rate, target);
    node.nodeValue = raw.replace(PRICE_RE, function () { return out; });
    el.setAttribute("title", "≈ " + fmtEUR(eur) + " · charged in EUR at checkout");
  }
  function isPriceish(el) {
    // only walk containers that plausibly hold a price (keeps us off prose)
    return el.closest && el.closest(
      ".product-price,.price,.price-item,.money,[data-price],[class*=price],[class*=Price]," +
      ".cart,[class*=cart],[class*=total],[class*=Total],.card,[class*=product]");
  }
  function scan(root) {
    if (!rate) return;
    try {
      var w = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
        acceptNode: function (n) {
          if (n.nodeValue.indexOf("€") === -1) return NodeFilter.FILTER_REJECT;
          if (!PRICE_RE.test(n.nodeValue)) {                 // node must be JUST a price…
            return inLabel(n.parentElement) && oneAmount(n.nodeValue)   // …or a Moon Bundle label
              ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT;
          }
          return isPriceish(n.parentElement) ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT;
        }
      });
      var nodes = [], n;
      while ((n = w.nextNode())) nodes.push(n);
      nodes.forEach(convertNode);
    } catch (e) {}
  }

  // ── transparency (no fixed bar → no clash with sticky add-to-cart) ──
  // One line under the product price and under the cart total, carrying the exact EUR amount
  // that will be debited. Readable, and no louder than that: it is reassurance, not a warning.
  // A neutral chip rather than small grey text: at 12px/0.7 opacity it read as legal filler and
  // went unnoticed. Same visual vocabulary as the theme's own badges, no alarm colour.
  var NOTE_CSS = "display:inline-block;margin-top:8px;padding:5px 10px;border-radius:6px;" +
                 "background:rgba(127,127,127,.14);font-size:12.5px;line-height:1.3;" +
                 "font-weight:500;letter-spacing:.1px;color:inherit;vertical-align:middle";
  var SKIP_EUR = "s,del,[class*=compare],[class*=Compare],[class*=old-price],[class*=oldPrice]," +
                 "[class*=unit-price],[class*=UnitPrice]";
  // Switzerland reads German, French or Italian, and a visitor of the English-speaking markets
  // can still be served another published locale (a fr-CA browser lands on /fr). A French page
  // with an English line under the price reads like a bug, so every published locale gets its
  // own wording.
  // Wording deliberately states the euro figure this price equals, not a promised total: on a
  // product page the shopper may then pick a 10-can bundle, and "you'll be charged X" would lie.
  var NOTE = {
    en: "Charged in EUR: {a}",
    fr: "Débité en EUR : {a}",
    de: "Abbuchung in EUR: {a}",
    nl: "Afgerekend in EUR: {a}",
    it: "Addebito in EUR: {a}",
    lt: "Nuskaitoma EUR: {a}",
    cs: "Účtováno v EUR: {a}"
  };
  function pageLocale() {
    try { if (window.Shopify && Shopify.locale) return String(Shopify.locale).slice(0, 2).toLowerCase(); } catch (e) {}
    return "en";
  }
  function noteParts(eur) {
    var p = (NOTE[pageLocale()] || NOTE.en).split("{a}");
    return { before: p[0], amount: fmtEUR(eur), after: p[1] || "" };
  }
  function fillNote(s, eur) {
    var p = noteParts(eur);
    var shown = s.querySelector("strong");
    // compare what is actually rendered, not just our bookkeeping: if anything ever rewrites the
    // amount, the line repairs itself instead of staying wrong for the rest of the session
    if (s.getAttribute("data-dp-amount") === p.amount && shown && shown.textContent === p.amount) return;
    s.setAttribute("data-dp-amount", p.amount);
    s.textContent = "";
    s.appendChild(document.createTextNode(p.before));
    var b = document.createElement("strong");
    b.style.fontWeight = "700";
    b.textContent = p.amount;
    s.appendChild(b);
    if (p.after) s.appendChild(document.createTextNode(p.after));
  }

  function isVisible(el) {
    // the theme renders hidden `.cart__total` mirrors for its own JS — never annotate those
    return !!el && !el.hidden && el.getAttribute("aria-hidden") !== "true" &&
           !(el.classList && el.classList.contains("hidden"));
  }
  function eurIn(el) {
    // the EUR value we converted inside this block, ignoring struck-through compare-at prices
    var c = [];
    if (el.hasAttribute("data-dp-eur")) c.push(el);
    c = c.concat([].slice.call(el.querySelectorAll("[data-dp-eur]")));
    for (var i = 0; i < c.length; i++) {
      if (c[i].closest && c[i].closest(SKIP_EUR)) continue;
      var v = parseFloat(c[i].getAttribute("data-dp-eur"));
      if (v > 0) return v;
    }
    return null;
  }
  function note(el) {
    if (!isVisible(el)) return false;
    var eur = eurIn(el);
    if (eur === null) return false;                            // nothing converted here (yet)
    var nx = el.nextElementSibling;
    if (nx && nx.classList && nx.classList.contains("dp-fx-note")) {
      fillNote(nx, eur);                                       // variant switch / cart update
      return true;
    }
    var s = document.createElement("span");
    s.className = "dp-fx-note";
    s.style.cssText = NOTE_CSS;
    fillNote(s, eur);
    el.insertAdjacentElement("afterend", s);
    return true;
  }
  function noteAll(selectors) {
    for (var i = 0; i < selectors.length; i++) {
      var els = document.querySelectorAll(selectors[i]), placed = false;
      for (var j = 0; j < els.length; j++) if (note(els[j])) placed = true;
      if (placed) return;                                       // first selector that lands wins
    }
  }
  // ── the hand-off to checkout (LABELLED currencies: Canada, 14/10/2026) ──
  // There the drawer button reads "Checkout C$58.00", then Shopify's checkout opens in euros
  // ("€36.40"): Canadians left right there, on the first checkout screen, before typing anything.
  // One line right above the button puts both figures side by side, the euro one written the way
  // the checkout writes it, so the amount waiting at checkout is one they have already read.
  // The only node we insert into Moon Bundle's DOM: it carries .dp-fx-note (the scanner never
  // converts it), is re-placed if Svelte re-creates the button, and goes when the button shows
  // no amount we converted.
  var HANDOFF = {
    en: "Checkout is in euros: {eur} ({loc} at today's rate) plus shipping. Your bank converts it at its own rate.",
    fr: "Le paiement se fera en euros : {eur}, soit {loc}, plus la livraison. Votre banque convertit à son taux."
  };
  var HANDOFF_CSS = "display:block;margin:0 0 8px;padding:7px 10px;border-radius:6px;" +
                    "background:rgba(127,127,127,.14);font-size:12.5px;line-height:1.35;" +
                    "font-weight:500;text-align:center;color:inherit";
  function checkoutEUR(n, loc) {                               // "€36.40" (en), "36,40 €" (fr)
    return loc === "fr" ? n.toFixed(2).replace(".", ",") + " €" : "€" + n.toFixed(2);
  }
  function fillHandoff(s, tpl, eur, local) {
    var b = s.querySelector("strong");
    if (s.getAttribute("data-dp-sig") === eur + "|" + local && b && b.textContent === eur) return;
    s.setAttribute("data-dp-sig", eur + "|" + local);
    var p = tpl.split("{eur}");
    s.textContent = "";
    s.appendChild(document.createTextNode(p[0]));
    b = document.createElement("strong");
    b.style.cssText = "font-weight:700;white-space:nowrap";
    b.textContent = eur;
    s.appendChild(b);
    s.appendChild(document.createTextNode((p[1] || "").replace("{loc}", function () { return local; })));
  }
  function handoff() {
    if (!LABELLED[target]) return;
    var loc = pageLocale(), tpl = HANDOFF[loc] || HANDOFF.en, keep = [];
    var btns = document.querySelectorAll(".moonbundle-cart-drawer-checkout");
    for (var i = 0; i < btns.length; i++) {
      var btn = btns[i], eur = parseFloat(btn.getAttribute("data-dp-label-eur"));
      var local = eur > 0 ? fmt(eur * rate, target) : "";
      if (!local || (btn.textContent || "").indexOf(local) === -1) continue;   // no amount (yet)
      var prev = btn.previousElementSibling;
      var s = prev && prev.classList && prev.classList.contains("dp-fx-handoff") ? prev : null;
      if (!s) {
        s = document.createElement("div");
        s.className = "dp-fx-note dp-fx-handoff";
        s.style.cssText = HANDOFF_CSS;
        btn.parentNode.insertBefore(s, btn);
      }
      fillHandoff(s, tpl, checkoutEUR(eur, loc), local);
      keep.push(s);
    }
    var all = document.querySelectorAll(".dp-fx-handoff");
    for (var j = 0; j < all.length; j++) if (keep.indexOf(all[j]) === -1) all[j].remove();
  }

  function ui() {
    // 1) Integrate the local currency into the store's EXISTING footer selector
    //    (natively shows "(EUR €)") — rewrite only the SELECTED label, never the country dropdown items.
    document.querySelectorAll(".localization-form__item-button .localization-form__currency").forEach(function (el) {
      if (el.getAttribute("data-dp-ccy")) return;
      el.setAttribute("data-dp-ccy", "1");
      // CHF's symbol IS its code: "(CHF)", not "(CHF CHF)"
      var sym = FMT[target].s.trim();
      el.textContent = "(" + target + (sym === target ? "" : " " + sym) + ")";
    });
    // 2) The EUR line — under the main product price (the theme wraps it in
    //    [data-update-block="price-compact"], OUTSIDE the add-to-cart form) and under the cart total.
    noteAll(['[data-update-block="price-compact"]', '[data-update-block^="price"]',
             '.product__info-wrapper .product-price', '.product-info .product-price',
             'form[action*="/cart/add"] .product-price']);
    noteAll(['.cart__total', '.totals__total', '.cart-drawer .totals__subtotal']);
    // 3) The euro total waiting at checkout, right above Moon Bundle's drawer button.
    handoff();
  }

  // ── boot ──────────────────────────────────────────────────
  withRates(function (fx) {
    if (!fx || fx.stale || !fx.rates || !fx.rates[target]) return;   // no rates / stale → stay EUR
    rate = fx.rates[target];
    var run = function () {
      scan(document.body); ui();
      // Rescan the WHOLE body, not just this batch's addedNodes: the debounce made every new
      // mutation cancel the previous timer, so earlier batches were dropped and never scanned.
      // Moon Bundle (Svelte) renders its tiers in several waves — that race is exactly why some
      // products showed dollars and others stayed in euros. A full rescan costs nothing: the
      // TreeWalker rejects any text node without a €, and converted nodes no longer have one.
      var t; var mo = new MutationObserver(function () {
        clearTimeout(t);
        t = setTimeout(function () { scan(document.body); ui(); }, 120);
      });
      // attributeFilter: a drawer or tab revealed by a class/aria-hidden flip must be rescanned;
      // our own data-dp-* writes are not in the filter, so they cannot feed the loop.
      mo.observe(document.body, { childList: true, subtree: true, characterData: true,
                                  attributes: true, attributeFilter: ["class", "aria-hidden"] });
      // A hidden tab throttles setTimeout, so the debounced rescan can be deferred for a long
      // time: open a product in a background tab, an app renders its prices there, and they are
      // still in euros when the shopper finally looks. Catch up as soon as the page is seen.
      document.addEventListener("visibilitychange", function () {
        if (!document.hidden) { scan(document.body); ui(); }
      });
    };
    if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", run);
    else run();
  });
})();
